/**
 * AdminLayout — shell do /admin no padrão shadcn (Sidebar colapsável + header
 * com breadcrumb + tema claro/escuro). Independente do layout do tenant.
 */
import { useEffect, useMemo } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Building2,
  Layers,
  Receipt,
  Activity,
  ScrollText,
  Users2,
  Youtube,
  Satellite,
  Plug,
  FolderTree,
  Webhook,
  Link2,
  Landmark,
  Map,
  Sparkles,
  Navigation,
  Cloud,
  Car,
  ReceiptText,
  Mail,
  FileCode2,
  Briefcase,
  Tag,
  Sun,
  BrainCircuit,
  Megaphone,
  Bug,
  CalendarRange,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { SidebarInset, SidebarProvider } from "@/components/ui/sidebar";
import { AdminSidebar } from "@/components/admin/shell/AdminSidebar";
import { AdminHeader } from "@/components/admin/shell/AdminHeader";
import { useAdminTheme } from "@/components/admin/shell/useAdminTheme";

export const adminNavGroups = [
  {
    label: "Acompanhamento",
    items: [
      { key: "my-day", path: "/admin/my-day", label: "Meu dia", icon: Sun, perm: "tenants.view" },
      { key: "dashboard", path: "/admin", label: "Dashboard", icon: LayoutDashboard, perm: "tenants.view" },
    ],
  },
  {
    label: "Cadastros",
    items: [
      { key: "clients", path: "/admin/clients", label: "Clientes", icon: Building2, perm: "tenants.view" },
      { key: "portfolios", path: "/admin/portfolios", label: "Carteiras", icon: Briefcase, perm: "tenants.view" },
  { key: "tags", path: "/admin/tags", label: "Etiquetas", icon: Tag, perm: "tags.manage" },
      { key: "professionals", path: "/admin/professionals", label: "Profissionais", icon: Activity, perm: "health.view" },
      { key: "users", path: "/admin/users", label: "Usuários", icon: Users2, perm: "team.manage" },
      { key: "plans", path: "/admin/plans", label: "Planos", icon: Layers, perm: "plans.view" },
      { key: "finance", path: "/admin/finance", label: "Financeiro", icon: Receipt, perm: "billing.view" },
    ],
  },
  {
    label: "Comunicação",
    items: [
      {
        key: "communications",
        path: "/admin/communications",
        label: "Campanhas",
        icon: Megaphone,
        perm: "communications.view",
      },
    ],
  },
  {
    label: "Configurações",
    items: [
      {
        key: "integrations",
        path: "/admin/integrations",
        label: "Integrações",
        icon: Plug,
        perm: "integrations.manage",
        children: [
          { key: "integrations-global", path: "/admin/integrations", label: "Globais", icon: Plug },
          { key: "integrations-google", path: "/admin/integrations/google-maps", label: "Google", icon: Map },
          { key: "integrations-openai", path: "/admin/integrations/openai", label: "OpenAI", icon: Sparkles },
          { key: "integrations-jev", path: "/admin/integrations/jev", label: "Jev", icon: BrainCircuit },
          { key: "integrations-here", path: "/admin/integrations/here", label: "HERE", icon: Navigation },
          { key: "integrations-s3", path: "/admin/integrations/s3", label: "Amazon S3", icon: Cloud },
          { key: "integrations-placas", path: "/admin/integrations/placas", label: "API Placas", icon: Car },
          { key: "integrations-focus", path: "/admin/integrations/focus-nfe", label: "Focus NFe", icon: ReceiptText },
          { key: "integrations-smtp", path: "/admin/integrations/smtp", label: "E-mail (SMTP)", icon: Mail },
          { key: "integrations-invoices", path: "/admin/integrations/invoices", label: "Notas Fiscais", icon: Landmark },
          { key: "integrations-positron", path: "/admin/integrations/positron", label: "Positron GPS", icon: Satellite },
          { key: "integrations-telli", path: "/admin/integrations/telli", label: "Telli (portais)", icon: Webhook },
        ],
      },
      { key: "support-videos", path: "/admin/support-videos", label: "Vídeos de Apoio", icon: Youtube, perm: "help.manage" },
      { key: "catalog", path: "/admin/service-catalog", label: "Catálogo de Serviços", icon: FolderTree, perm: "catalog.manage" },
      { key: "portal-map", path: "/admin/portal-service-map", label: "De-para dos Portais", icon: Link2, perm: "catalog.manage" },
      { key: "ipva-calendar", path: "/admin/ipva-calendar", label: "Calendário do IPVA", icon: CalendarRange, perm: "catalog.manage" },
      { key: "banks", path: "/admin/banks", label: "Bancos", icon: Landmark, perm: "catalog.manage" },
      { key: "captures", path: "/admin/captures", label: "Capturas", icon: FileCode2, perm: "audit.view" },
      { key: "jev-classifications", path: "/admin/jev-classifications", label: "Classificações", icon: BrainCircuit, perm: "integrations.manage" },
      { key: "audit", path: "/admin/audit", label: "Auditoria", icon: ScrollText, perm: "audit.view" },
    ],
  },
  {
    label: "Outros",
    items: [
      { key: "errors", path: "/admin/erros", label: "Erros", icon: Bug, perm: "audit.view" },
    ],
  },
];

const PAPEIS_ADMIN = ["super_admin", "support_admin", "billing_admin"];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { theme, setTheme } = useAdminTheme();

  useEffect(() => {
    if (user?.role && !PAPEIS_ADMIN.includes(user.role)) navigate("/dashboard", { replace: true });
  }, [user, navigate]);

  const grupos = useMemo(() => {
    const permitidas = user?.admin_permissions || [];
    return adminNavGroups
      .map((g) => ({ ...g, items: g.items.filter((i) => !i.perm || permitidas.includes(i.perm)) }))
      .filter((g) => g.items.length);
  }, [user]);

  if (!user) return null;

  return (
    <SidebarProvider defaultOpen={document.cookie.indexOf("sidebar_state=false") === -1}>
      <AdminSidebar grupos={grupos} user={user} onLogout={logout} />
      <SidebarInset className="min-w-0">
        <AdminHeader grupos={grupos} theme={theme} setTheme={setTheme} />
        <div className="admin-shell flex flex-1 flex-col gap-4 p-4 md:p-6 min-w-0" data-testid="main-content">
          <Outlet />
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
}
